import React, { useState, useEffect, useMemo } from "react";
import { CartProduct } from "../../assets/icons/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

// ===== Configs & Utils =====
const API_BASE = "https://laravel-api.aj-mart.com";
const STORAGE_BASE = `${API_BASE}/storage/app/public/`;
const PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='800' height='600'><rect width='100%' height='100%' fill='%23f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='20'>No Image</text></svg>";

const safeStr = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));
const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const normalizeString = (str) => (str ? String(str).toLowerCase().trim() : "");

// تقسيم الوصف بطريقة آمنة (نفس منطق SubCategories)
const splitDesc = (text, lang) => {
  const s = safeStr(text).trim();
  if (!s) return [];
  if (lang === "ar") {
    return s
      .split(/[|،؛•\-–—]+|\r?\n/)
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return s
    .split(/\r?\n|[|•\-–—]+/)
    .map((x) => x.trim())
    .filter(Boolean);
};

// Shuffle ثابت (لترتيب عشوائي)
const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export default function ProductGrid() {
  const { t, i18n } = useTranslation();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCapacity, setSelectedCapacity] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // ===== Fetch Data (موحّد مثل SubCategories) =====
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/products`, { withCredentials: true });
        const list = Array.isArray(data?.products) ? data.products : [];
        const normalized = list.map((p) => ({
          ...p,
          product_capacity_array: Array.isArray(p.product_capacity_array)
            ? p.product_capacity_array
            : typeof p.product_capacity_array === "string"
            ? p.product_capacity_array.split(",").map((it) => it.trim())
            : [],
        }));
        setProducts(normalized);
      } catch (e) {
        console.error("Error fetching products:", e);
        setProducts([]);
      }
    };

    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/categories`);
        const src = Array.isArray(data?.category) ? data.category : [];
        // إزالة التكرار بالاسم الإنجليزي (مثل SubCategories)
        const unique = Array.from(
          new Map(src.map((it) => [normalizeString(it.category_name_E), it])).values()
        );
        setCategories(unique);
      } catch (e) {
        console.error("Error fetching categories:", e);
        setCategories([]);
      }
    };

    const fetchSubCategories = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/subCategory`);
        const src = Array.isArray(data?.sub_category) ? data.sub_category : [];
        const unique = Array.from(
          new Map(src.map((it) => [normalizeString(it.sub_category_name_E), it])).values()
        );
        setSubCategories(unique);
      } catch (e) {
        console.error("Error fetching subcategories:", e);
        setSubCategories([]);
      }
    };

    const fetchBrands = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/brand`);
        const src = Array.isArray(data?.brands) ? data.brands : [];
        const unique = Array.from(
          new Map(src.map((it) => [normalizeString(it.brand_name), it])).values()
        );
        setBrands(unique);
      } catch (e) {
        console.error("Error fetching brands:", e);
        setBrands([]);
      }
    };

    const fetchAll = async () => {
      setIsLoading(true);
      await Promise.all([fetchProducts(), fetchCategories(), fetchSubCategories(), fetchBrands()]);
      setIsLoading(false);
    };

    fetchAll();
  }, []);

  // Reset selectedCapacity when category is Airpods or Charger
  useEffect(() => {
    if (selectedCategory === "Airpods" || selectedCategory === "Charger") {
      setSelectedCapacity("");
    }
  }, [selectedCategory]);

  const getLocalizedText = (key) => (i18n.language === "ar" ? safeStr(key.A) : safeStr(key.E));

  // ===== Filtering (مطابق SubCategories) =====
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (selectedBrand) {
        const brandOk = safeStr(p.product_brand_name) === selectedBrand;
        const capOk = selectedCapacity
          ? Array.isArray(p.product_capacity_array) &&
            p.product_capacity_array.some(
              (cap) => normalizeString(cap) === normalizeString(selectedCapacity)
            )
          : true;
        return brandOk && capOk;
      }

      const subOk = selectedSubCategory
        ? normalizeString(p.product_sub_category) === normalizeString(selectedSubCategory)
        : true;
      const catOk = selectedCategory
        ? normalizeString(p.product_category) === normalizeString(selectedCategory)
        : true;
      const capOk = selectedCapacity
        ? Array.isArray(p.product_capacity_array) &&
          p.product_capacity_array.some(
            (cap) => normalizeString(cap) === normalizeString(selectedCapacity)
          )
        : true;

      return subOk && catOk && capOk;
    });
  }, [products, selectedBrand, selectedCapacity, selectedCategory, selectedSubCategory]);

  // ===== Random Order (يتغير عند تغيّر الفلاتر فقط) =====
  const shuffledProducts = useMemo(() => shuffleArray(filteredProducts), [filteredProducts]);

  const totalPages = Math.ceil(shuffledProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = useMemo(
    () => shuffledProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [shuffledProducts, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedSubCategory, selectedBrand, selectedCapacity]);

  useEffect(() => {
    if (currentPage > Math.max(1, totalPages)) setCurrentPage(Math.max(1, totalPages));
  }, [currentPage, totalPages]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {/* الهيدر */}
      <section className="landingPages">
        <div className="container h-full flex flex-col gap-2 justify-center">
          <div className="text-[22px] sm:text-[28px] md:text-[35px] font-bold text-white">
            {t("All Product")}
          </div>
          <div className="flex flex-wrap items-center gap-1 text-white text-sm sm:text-base">
            <Link to={"/"} className="hover:underline">
              {t("Home")}
            </Link>
            <span>-</span>
            <div>{t("All Product")}</div>
          </div>
        </div>
      </section>

      {/* الفلاتر + النتائج بنفس ستايل SubCategories */}
      <section className="relative py-6 sm:py-10">
        <div className="container flex flex-col gap-8 sm:gap-12">
          {isLoading ? (
            <p>{t("Loading...")}</p>
          ) : (
            <div className="w-full flex flex-col gap-6 sm:gap-10">
              {/* Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {/* Category */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1485a6] font-bold text-sm sm:text-base">
                    {t("Categories")}
                  </label>
                  <select
                    className="w-full p-2 rounded-md outline-none border border-[#1485a6] bg-white/70 text-[#374151]"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">{t("Show All Categories")}</option>
                    {categories.map((item) => (
                      <option key={item.category_id} value={item.category_name_E}>
                        {getLocalizedText({
                          A: item.category_name_A,
                          E: item.category_name_E,
                        })}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sub Category */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1485a6] font-bold text-sm sm:text-base">
                    {t("Sub Categories")}
                  </label>
                  <select
                    className="w-full p-2 rounded-md outline-none border border-[#1485a6] bg-white/70 text-[#374151]"
                    value={selectedSubCategory}
                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                  >
                    <option value="">{t("Show All Sub Categories")}</option>
                    {subCategories.map((item) => (
                      <option key={item.sub_category_id} value={item.sub_category_name_E}>
                        {getLocalizedText({
                          A: item.sub_category_name_A,
                          E: item.sub_category_name_E,
                        })}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Brand */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1485a6] font-bold text-sm sm:text-base">
                    {t("Brand")}
                  </label>
                  <select
                    className="w-full p-2 rounded-md outline-none border border-[#1485a6] bg-white/70 text-[#374151]"
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                  >
                    <option value="">{t("Show All Brands")}</option>
                    {brands.map((item) => (
                      <option key={item.brand_id} value={item.brand_name}>
                        {item.brand_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Capacity */}
                {selectedCategory !== "Airpods" && selectedCategory !== "Charger" && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[#1485a6] font-bold text-sm sm:text-base">
                      {t("Capacity")}
                    </label>
                    <select
                      className="w-full p-2 rounded-md outline-none border border-[#1485a6] bg-white/70 text-[#374151]"
                      value={selectedCapacity}
                      onChange={(e) => setSelectedCapacity(e.target.value)}
                    >
                      <option value="">{t("Show All Capacities")}</option>
                      <option value="64 GB">64 GB</option>
                      <option value="128 GB">128 GB</option>
                      <option value="256 GB">256 GB</option>
                      <option value="512 GB">512 GB</option>
                      <option value="1 T">1 T</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Results Grid (مطابق SubCategories) */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
                {shuffledProducts.length > 0 ? (
                  paginatedProducts.map((product, index) => {
                    const lang = i18n.language;

                    const productName = getLocalizedText({
                      A: product.product_name_A,
                      E: product.product_name_E,
                    });
                    const productDesc = getLocalizedText({
                      A: product.product_desc_A,
                      E: product.product_desc_E,
                    });
                    const descItems = splitDesc(productDesc, lang).slice(0, 2);

                    const price = toNum(product.product_price, 0);
                    const discountPct = Math.max(0, toNum(product.product_price_discount, 0));
                    const priceAfterDiscount = (price * (1 - discountPct / 100)).toFixed(2);

                    const inventoryClass =
                      toNum(product.product_stock_available, 0) === 0
                        ? "text-red-500"
                        : "text-[#18AB00]";
                    const isHovered = hoveredIndex === index;

                    const imgSrc = safeStr(product.product_main_img)
                      ? `${STORAGE_BASE}${safeStr(product.product_main_img).replace(/^\/+/, "")}`
                      : PLACEHOLDER;

                    return (
                      <div
                        key={product.product_id ?? `${product.product_name_E}-${index}`}
                        className="group cursor-pointer flex flex-col w-full rounded-md shadow-md outline outline-[2px] outline-solid relative bg-white"
                        style={{ outlineColor: "#eee" }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        <Link to={`/productdetails/${product.product_id ?? ""}`}>
                          <div className="w-full relative">
                            {/* aspect لتوحيد الارتفاع */}
                            <div className="w-full aspect-[4/3] overflow-hidden rounded-t-md">
                              <img
                                className="w-full h-full object-cover"
                                src={imgSrc}
                                alt={safeStr(product.product_name_E) || "product"}
                                onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                              />
                            </div>

                            {discountPct > 0 && (
                              <div className="dis-grid w-9 h-9 sm:w-10 sm:h-10 bg-[#1485a6] rounded-full text-white font-bold flex items-center justify-center absolute top-2 right-2">
                                {discountPct.toFixed(0)}%
                              </div>
                            )}
                          </div>
                        </Link>

                        <div className="h-[2px] bg-[#e5e7eb]" />

                        <div className="py-2 sm:py-3 flex flex-col gap-2 sm:gap-3 px-3 sm:px-4 pb-12 sm:pb-14">
                          <div className="flex items-center justify-between gap-2">
                            <div className="name-grid text-[13px] sm:text-[15px] md:text-[16px] font-bold line-clamp-2">
                              {productName}
                            </div>
                            <div className="new-grid min-w-[56px] h-[19px] bg-[#1485a6] text-white capitalize font-bold rounded-tr-[100px] rounded-bl-[100px] flex items-center justify-center text-[10px] px-2 shrink-0">
                              {safeStr(product.product_status)}
                            </div>
                          </div>

                          <ul className="text-[10px] sm:text-[11px] text-[#666666] list-disc pl-4 space-y-1">
                            {descItems.map((desc, idx) => (
                              <li key={idx} className="leading-snug">
                                {desc.length > 75 ? desc.substring(0, 75) + "..." : desc}
                              </li>
                            ))}
                          </ul>

                          <div className="flex items-end justify-between">
                            <div className="flex flex-col gap-1">
                              <span className="font-bold text-[13px] sm:text-[14px]">
                                {priceAfterDiscount} <span>{t("JOD")}</span>
                              </span>
                              {discountPct > 0 && (
                                <span className="line-through text-red-500 text-[11px] sm:text-[12px]">
                                  {price.toFixed(2)} <span>{t("JOD")}</span>
                                </span>
                              )}
                            </div>
                            <div className={`font-bold capitalize text-[12px] sm:text-[14px] ${inventoryClass}`}>
                              {t("In Stock")}
                            </div>
                          </div>
                        </div>

                        {/* زر الكارت: دائم على الموبايل، Hover على الشاشات الكبيرة (نفس SubCategories) */}
                        <div
                          className={`bg-[#1485a6] flex p-2 sm:p-2.5 items-center justify-center gap-1.5 text-white font-bold rounded-tl-[20px] rounded-br-[20px] absolute bottom-0 left-0 right-0 
                          opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300`}
                        >
                          <CartProduct />
                          <Link to={`/productdetails/${product.product_id ?? ""}`}>
                            {t("Add To Cart")}
                          </Link>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="col-span-full text-center">
                    {t("No products match the selected filters.")}
                  </p>
                )}
              </div>

              {totalPages > 1 && (
                <div className="flex flex-wrap justify-center items-center gap-2 mt-6" dir="ltr">
                  <button type="button" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                    className="px-3 py-2 border rounded-md disabled:opacity-40 hover:bg-gray-100">
                    {i18n.language === "ar" ? "السابق" : "Previous"}
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button key={page} type="button" onClick={() => goToPage(page)}
                      aria-current={currentPage === page ? "page" : undefined}
                      className={`min-w-[36px] px-2 py-2 border rounded-md ${currentPage === page ? "bg-[#1485a6] text-white border-[#1485a6]" : "hover:bg-gray-100"}`}>
                      {page}
                    </button>
                  ))}
                  <button type="button" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                    className="px-3 py-2 border rounded-md disabled:opacity-40 hover:bg-gray-100">
                    {i18n.language === "ar" ? "التالي" : "Next"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
